import React, { forwardRef, useImperativeHandle, useRef, useEffect } from 'react';
import './RichEditor.css';

// Turn raw {{token}} occurrences (in TEXT nodes only — never inside an existing pill's
// data-var attribute) into the same styled pills the toolbar inserts, so pre-existing
// template wording renders consistently with newly-inserted variables.
export function hydrateVarPills(root) {
  const RE = /\{\{([^{}]+)\}\}/g;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
  const targets = [];
  let node;
  while ((node = walker.nextNode())) { if (/\{\{[^{}]+\}\}/.test(node.nodeValue)) targets.push(node); }
  for (const t of targets) {
    const text = t.nodeValue;
    const frag = document.createDocumentFragment();
    let last = 0, m;
    RE.lastIndex = 0;
    while ((m = RE.exec(text))) {
      if (m.index > last) frag.appendChild(document.createTextNode(text.slice(last, m.index)));
      const span = document.createElement('span');
      span.className = 're-var';
      span.setAttribute('contenteditable', 'false');
      span.setAttribute('data-var', `{{${m[1]}}}`);
      span.textContent = m[1];
      frag.appendChild(span);
      last = m.index + m[0].length;
    }
    if (last < text.length) frag.appendChild(document.createTextNode(text.slice(last)));
    t.parentNode.replaceChild(frag, t);
  }
}

// A tiny, dependency-free rich-text editor over a contentEditable div.
// - Stores/returns HTML via onChange.
// - Uncontrolled while typing (we only load innerHTML when `docKey` changes) so the
//   caret never jumps; the parent persists onChange output to storage/state.
// - Exposes insertText() so the variable chips can drop {{tokens}} at the caret.
const RichEditor = forwardRef(function RichEditor({ docKey, value = '', onChange, placeholder, varPills = false }, ref) {
  const elRef = useRef(null);

  // Load content when the active reminder/language changes — NOT on every keystroke.
  useEffect(() => {
    if (elRef.current && elRef.current.innerHTML !== (value || '')) {
      elRef.current.innerHTML = value || '';
      if (varPills) hydrateVarPills(elRef.current);   // show raw {{tokens}} as pills too
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docKey]);

  const emit = () => { if (elRef.current && onChange) onChange(elRef.current.innerHTML); };

  function exec(cmd, arg) {
    const el = elRef.current; if (!el) return;
    el.focus();
    document.execCommand(cmd, false, arg);
    emit();
  }
  function addLink() {
    const url = window.prompt('Link URL:', 'https://');
    if (url) exec('createLink', url);
  }

  function placeCaretInEditor() {
    const el = elRef.current;
    el.focus();
    const sel = window.getSelection();
    if (!sel.rangeCount || !el.contains(sel.anchorNode)) {
      const r = document.createRange(); r.selectNodeContents(el); r.collapse(false);
      sel.removeAllRanges(); sel.addRange(r);
    }
  }

  useImperativeHandle(ref, () => ({
    insertText(text) {
      if (!elRef.current) return;
      placeCaretInEditor();
      document.execCommand('insertText', false, text);
      emit();
    },
    // Insert a variable as a styled, atomic "pill". The pill shows the label; the raw
    // {{token}} lives in data-var and is what gets substituted when the message is sent
    // (the pill formatting is dropped on send — see tokensToRaw in RunReminders).
    // Built via DOM + Range (not execCommand('insertHTML')): Safari block-splits
    // contenteditable="false" elements inserted through insertHTML, which put the
    // pill on its own line on Mac. Range.insertNode keeps it inline everywhere.
    insertVar(token, label) {
      if (!elRef.current) return;
      placeCaretInEditor();
      const sel = window.getSelection();
      if (!sel.rangeCount) return;
      const range = sel.getRangeAt(0);
      range.deleteContents();
      const span = document.createElement('span');
      span.className = 're-var';
      span.setAttribute('contenteditable', 'false');
      span.setAttribute('data-var', String(token));
      span.textContent = String(label || token);
      const space = document.createTextNode(' ');
      range.insertNode(space);
      range.insertNode(span);
      range.setStartAfter(space);   // caret lands after the pill + space
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);
      emit();
    },
    // Drop arbitrary HTML (e.g. a profile link) at the caret.
    insertHTML(html) {
      if (!elRef.current) return;
      placeCaretInEditor();
      document.execCommand('insertHTML', false, html);
      emit();
    },
  }));

  // Keep the editor selection when clicking a toolbar button (mousedown would blur it).
  const keep = e => e.preventDefault();

  return (
    <div className="re-wrap">
      <div className="re-toolbar">
        <button type="button" className="re-tb" title="Bold" onMouseDown={keep} onClick={() => exec('bold')}><b>B</b></button>
        <button type="button" className="re-tb" title="Italic" onMouseDown={keep} onClick={() => exec('italic')}><i>I</i></button>
        <button type="button" className="re-tb" title="Underline" onMouseDown={keep} onClick={() => exec('underline')}><u>U</u></button>
        <span className="re-sep" />
        <button type="button" className="re-tb" title="Bulleted list" onMouseDown={keep} onClick={() => exec('insertUnorderedList')}>• List</button>
        <button type="button" className="re-tb" title="Numbered list" onMouseDown={keep} onClick={() => exec('insertOrderedList')}>1. List</button>
        <span className="re-sep" />
        <button type="button" className="re-tb" title="Add link" onMouseDown={keep} onClick={addLink}>🔗 Link</button>
        <button type="button" className="re-tb" title="Clear formatting" onMouseDown={keep} onClick={() => exec('removeFormat')}>⌫ Clear</button>
      </div>
      <div
        ref={elRef}
        className="re-editor"
        contentEditable
        suppressContentEditableWarning
        data-placeholder={placeholder || ''}
        onInput={emit}
      />
    </div>
  );
});

export default RichEditor;
